import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Sparkles, Brain, AlertTriangle, Lightbulb, BookOpen, Target, ArrowRight, Loader2, Info } from 'lucide-react';
import { leadsAPI } from '../../../api/apiHelper';

export default function AIInsights() {
    const { id } = useParams();
    const dealId = id;
    const [activeTab, setActiveTab] = useState("Executive Deal Summary");
    const [insightsData, setInsightsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAIInsights = async () => {
            if (!dealId || dealId === 'view') {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await leadsAPI.getDealAIInsights(dealId);

                if (response && response.data) {
                    setInsightsData(response.data);
                } else if (response && response.summary) { // Fallback if direct object
                    setInsightsData(response);
                } else {
                    console.warn("Unexpected AI insights data structure:", response);
                    setInsightsData(null);
                }

            } catch (err) {
                console.error("Error fetching AI insights:", err);
                setError("Failed to load AI insights.");
            } finally {
                setLoading(false);
            }
        };

        fetchAIInsights();
    }, [dealId]);

    const tabs = [
        "Executive Deal Summary",
        "All Risks Ranked",
        "Recommended Actions",
        "Sales Playbook Suggestions"
    ];

    const renderLoadingState = () => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '300px',
            color: '#6b7280'
        } }>
            <Loader2 className="animate-spin" size={ 32 } style={ { marginBottom: '16px', color: '#3b82f6' } } />
            <div style={ { fontSize: '14px', fontWeight: 500 } }>Loading AI insights...</div>
        </div>
    );

    const renderErrorState = () => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '300px',
            color: '#ef4444',
            textAlign: 'center',
            padding: '20px'
        } }>
            <AlertTriangle size={ 32 } style={ { marginBottom: '16px' } } />
            <div style={ { fontSize: '14px', fontWeight: 500, marginBottom: '8px' } }>Unable to load insights</div>
            <div style={ { fontSize: '12px', color: '#6b7280' } }>
                { error || "Please check your connection and try again." }
            </div>
            <button
                onClick={ () => window.location.reload() }
                style={ {
                    marginTop: '16px',
                    padding: '8px 16px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    color: '#374151'
                } }
            >
                Retry
            </button>
        </div>
    );

    const renderEmptyState = (message) => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            color: '#9ca3af',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px dashed #e5e7eb',
            textAlign: 'center',
            flex: 1
        } }>
            <div style={ { opacity: 0.5, marginBottom: '12px' } }><Info size={ 24 } /></div>
            <div style={ { fontSize: '14px', fontWeight: 500 } }>{ message }</div>
        </div>
    );

    const renderContent = () => {
        if (loading) return renderLoadingState();
        if (error) return renderErrorState();
        if (!insightsData) return renderEmptyState("No input");

        switch (activeTab) {
            case "Executive Deal Summary":
                const summary = insightsData.summary;
                if (!summary) return renderEmptyState("No input");

                return (
                    <div style={ { padding: '24px', background: '#eff6ff', borderRadius: '12px', border: '1px solid #dbeafe', display: 'flex', gap: '20px', alignItems: 'flex-start' } }>
                        <div style={ { flexShrink: 0, width: '40px', height: '40px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' } }>
                            <Brain size={ 20 } color="#2563eb" />
                        </div>
                        <div>
                            <h3 style={ { margin: '0 0 10px 0', fontSize: '18px', fontWeight: 700, color: '#1e3a8a' } }>AI Summary</h3>
                            <p style={ { margin: 0, fontSize: '14px', lineHeight: '1.7', color: '#1e40af' } }>{ summary }</p>
                        </div>
                    </div>
                );

            case "All Risks Ranked":
                const risks = insightsData.allRisksRanked;
                if (!risks || !Array.isArray(risks) || risks.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '16px' } }>
                        { risks.map((risk, idx) => {
                            const severity = risk.severity_level || 'Medium';
                            let color = '#f59e0b'; // Medium
                            let bgColor = '#fffbeb';
                            let textColor = '#d97706';

                            if (severity.toLowerCase().includes('high')) {
                                color = '#ef4444';
                                bgColor = '#fef2f2';
                                textColor = '#ef4444';
                            } else if (severity.toLowerCase().includes('low')) {
                                color = '#10b981';
                                bgColor = '#ecfdf5';
                                textColor = '#059669';
                            }

                            return (
                                <div key={ idx } style={ {
                                    borderLeft: `4px solid ${color}`,
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderLeftWidth: '4px',
                                    borderRadius: '4px 8px 8px 4px',
                                    padding: '16px 20px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                } }>
                                    <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' } }>
                                        <div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
                                            <AlertTriangle size={ 18 } color={ color } />
                                            <span style={ { fontSize: '15px', fontWeight: 700, color: '#111827' } }>{ risk.risk_name }</span>
                                        </div>
                                        <span style={ {
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            background: bgColor,
                                            color: textColor
                                        } }>{ risk.severity_level } Impact</span>
                                    </div>
                                    <p style={ { margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#4b5563' } }>{ risk.risk_description }</p>
                                    { risk.impact_area && (
                                        <div style={ { marginTop: '8px', fontSize: '12px', color: '#6b7280' } }>
                                            <strong>Impact Area:</strong> { risk.impact_area }
                                        </div>
                                    ) }
                                </div>
                            );
                        }) }
                    </div>
                );

            case "Recommended Actions":
                const actions = insightsData.recommendedActions;
                if (!actions || !Array.isArray(actions) || actions.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '16px' } }>
                        { actions.map((action, idx) => (
                            <div key={ idx } style={ {
                                background: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                display: 'flex',
                                gap: '20px',
                                alignItems: 'flex-start'
                            } }>
                                <div style={ { width: '40px', height: '40px', borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }>
                                    <Lightbulb size={ 20 } color="#d97706" />
                                </div>
                                <div style={ { flex: 1 } }>
                                    <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' } }>
                                        <h4 style={ { margin: '0 0 6px 0', fontSize: '15px', fontWeight: 700, color: '#111827' } }>{ action.action_title }</h4>
                                        { action.priority_level && (
                                            <span style={ { fontSize: '11px', fontWeight: 600, color: action.priority_level === 'High' ? '#ef4444' : '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px', whiteSpace: 'nowrap' } }>
                                                { action.priority_level }
                                            </span>
                                        ) }
                                    </div>
                                    <p style={ { margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#4b5563' } }>{ action.action_description }</p>
                                    { action.suggested_owner && (
                                        <div style={ { marginTop: '6px', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' } }>
                                            Owner: { action.suggested_owner }
                                        </div>
                                    ) }
                                </div>
                            </div>
                        )) }
                    </div>
                );

            case "Sales Playbook Suggestions":
                const playbooks = insightsData.salesPlaybookSuggestions;
                if (!playbooks || !Array.isArray(playbooks) || playbooks.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' } }>
                        { playbooks.map((playbook, idx) => (
                            <div key={ idx } style={ {
                                background: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px'
                            } }>
                                <div style={ { display: 'flex', alignItems: 'center', gap: '10px' } }>
                                    <BookOpen size={ 18 } color="#7c3aed" />
                                    <h4 style={ { margin: 0, fontSize: '15px', fontWeight: 700, color: '#111827' } }>{ playbook.suggestion_title }</h4>
                                </div>
                                <div style={ { fontSize: '12px', color: '#6b7280', fontWeight: '500' } }>{ playbook.stage_focus } Stage</div>
                                <p style={ { margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#4b5563', flex: 1 } }>{ playbook.suggested_tactic }</p>
                                <div style={ { fontSize: '12px', color: '#059669', background: '#ecfdf5', padding: '8px', borderRadius: '6px', marginTop: '4px' } }>
                                    <strong>Outcome:</strong> { playbook.expected_outcome }
                                </div>
                            </div>
                        )) }
                    </div>
                );

            default:
                return <div>Select a tab</div>;
        }
    };

    return (
        <div style={ { background: '#fff', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }>

            {/* Internal Tabs Navigation */ }
            <div style={ { display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 16px', background: '#f9fafb', overflowX: 'auto', scrollbarWidth: 'none' } }>
                { tabs.map(tab => (
                    <div
                        key={ tab }
                        onClick={ () => setActiveTab(tab) }
                        style={ {
                            padding: '12px 16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: activeTab === tab ? '#2563eb' : '#6b7280',
                            borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                        } }
                    >
                        { tab }
                    </div>
                )) }
            </div>

            {/* Content Area */ }
            <div style={ { flex: 1, padding: '16px', overflowY: 'auto' } }>
                { renderContent() }
            </div>

        </div>
    );
}
